import { ChevronDownIcon } from "@radix-ui/react-icons";
import { Slot } from "@radix-ui/react-slot";
import { VariantProps, cva } from "class-variance-authority";
import { PanelLeft } from "lucide-react";
import * as React from "react";
import { mergeProps } from "react-aria";

import { useIsMobile } from "@/components/hooks/use-mobile";
import { Button, ButtonProps } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

const SIDEBAR_COOKIE_NAME = "sidebar:state";
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
const SIDEBAR_WIDTH = "280px";
const SIDEBAR_WIDTH_MOBILE = "18rem";
const SIDEBAR_WIDTH_ICON = "3rem";

type SidebarContext = {
  state: "expanded" | "collapsed";
  open: boolean;
  setOpen: (open: boolean) => void;
  openMobile: boolean;
  setOpenMobile: (open: boolean) => void;
  isMobile: boolean;
  toggleSidebar: () => void;
};

const SidebarContext = React.createContext<SidebarContext | null>(null);

function useSidebar() {
  const context = React.use(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider.");
  }

  return context;
}

const SidebarProvider = ({
  ref,
  defaultOpen = true,
  open: openProp,
  onOpenChange: setOpenProp,
  className,
  style,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) => {
  const isMobile = useIsMobile();
  const [openMobile, setOpenMobile] = React.useState(false);

  // This is the internal state of the sidebar.
  // We use openProp and setOpenProp for control from outside the component.
  const [_open, setOpen] = React.useState(defaultOpen);
  const open = openProp ?? _open;
  const onOpenChange = React.useCallback(
    (value: boolean | ((value: boolean) => boolean)) => {
      const openState = typeof value === "function" ? value(open) : value;
      if (setOpenProp) {
        setOpenProp(openState);
      } else {
        setOpen(openState);
      }
    },
    [setOpenProp, open],
  );

  // This sets the cookie to keep the sidebar state.
  const cookie = `${SIDEBAR_COOKIE_NAME}=${_open}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
  React.useEffect(() => {
    document.cookie = cookie;
  }, [cookie]);

  // Helper to toggle the sidebar.
  const toggleSidebar = React.useCallback(() => {
    return isMobile
      ? setOpenMobile((open) => !open)
      : onOpenChange((open) => !open);
  }, [isMobile, onOpenChange, setOpenMobile]);

  // We add a state so that we can do data-state="expanded" or "collapsed".
  // This makes it easier to style the sidebar with Tailwind classes.
  const state = open ? "expanded" : "collapsed";

  const contextValue = React.useMemo<SidebarContext>(
    () => ({
      state,
      open,
      setOpen: onOpenChange,
      isMobile,
      openMobile,
      setOpenMobile,
      toggleSidebar,
    }),
    [
      state,
      open,
      onOpenChange,
      isMobile,
      openMobile,
      setOpenMobile,
      toggleSidebar,
    ],
  );

  return (
    <SidebarContext value={contextValue}>
      <TooltipProvider>
        <div
          style={
            {
              "--sidebar-width": SIDEBAR_WIDTH,
              "--sidebar-width-icon": SIDEBAR_WIDTH_ICON,
              ...style,
            } as React.CSSProperties
          }
          className={cn("group/sidebar-wrapper flex w-full", className)}
          ref={ref}
          {...props}
        >
          {children}
        </div>
      </TooltipProvider>
    </SidebarContext>
  );
};
SidebarProvider.displayName = "SidebarProvider";

const Sidebar = ({
  ref,
  side = "left",
  variant = "sidebar",
  collapsible = "offcanvas",
  className,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  side?: "left" | "right";
  variant?: "sidebar" | "floating" | "inset";
  collapsible?: "offcanvas" | "icon";
}) => {
  const { isMobile, state, openMobile, setOpenMobile } = useSidebar();

  if (isMobile) {
    return (
      <Sheet open={openMobile} onOpenChange={setOpenMobile} {...props}>
        <SheetContent
          data-sidebar="sidebar"
          data-mobile="true"
          className="max-h-screen w-(--sidebar-width) bg-slate-subtle p-0 text-slate-dim [&>button]:hidden"
          style={
            {
              "--sidebar-width": SIDEBAR_WIDTH_MOBILE,
            } as React.CSSProperties
          }
          side={side}
        >
          <aside aria-label="Sidebar" className="h-full">
            <div className="flex h-full w-full flex-col">{children}</div>
          </aside>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <aside
      aria-label="Sidebar"
      ref={ref}
      className="group peer hidden md:block text-slate-dim max-h-screen sticky top-0 min-h-screen overflow-auto"
      data-state={state}
      data-collapsible={state === "collapsed" ? collapsible : ""}
      data-variant={variant}
      data-side={side}
    >
      {/* This is what handles the sidebar gap on desktop */}
      <div
        className={cn(
          "duration-200 relative w-(--sidebar-width) bg-transparent transition-[width] ease-linear",
          "group-data-[collapsible=offcanvas]:w-0",
          "group-data-[side=right]:rotate-180",
          variant === "floating" || variant === "inset"
            ? "group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4)))]"
            : "group-data-[collapsible=icon]:w-(--sidebar-width-icon)",
        )}
      />
      <div
        className={cn(
          "h-full",
          "duration-200 hidden w-(--sidebar-width) transition-[left,right,width] ease-linear md:flex",
          side === "left"
            ? "left-0 group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]"
            : "right-0 group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)]",
          // Adjust the padding for floating and inset variants.
          variant === "floating" || variant === "inset"
            ? "group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4))+2px)]"
            : "group-data-[collapsible=icon]:w-(--sidebar-width-icon)",
          variant === "floating" && "p-2",
          className,
        )}
        {...props}
      >
        <div
          data-sidebar="sidebar"
          className={cn(
            "flex h-full w-full flex-col group-data-[variant=floating]:rounded-lg group-data-[variant=floating]:border group-data-[variant=floating]:border-slate-6 dark:group-data-[variant=floating]:border-slatedark-6 dark:border-slatedark-6 group-data-[variant=floating]:shadow-sm",
          )}
        >
          {children}
        </div>
      </div>
    </aside>
  );
};
Sidebar.displayName = "Sidebar";

const SidebarTrigger = ({
  className,
  onPress,
  fixed,
  static: isStatic,
  ...props
}: ButtonProps & {
  fixed?: boolean;
  static?: boolean;
}) => {
  const { toggleSidebar } = useSidebar();

  return (
    <Button
      data-sidebar="trigger"
      variant="ghost"
      size="icon"
      className={cn(
        "h-7 w-7 md:hidden",
        fixed
          ? "fixed left-2 top-[4.4rem]"
          : isStatic
          ? ""
          : "absolute left-2 top-2",
        className,
      )}
      onClick={() => {
        onPress?.();
        toggleSidebar();
      }}
      {...props}
    >
      <PanelLeft />
      <span className="sr-only">Toggle Sidebar</span>
    </Button>
  );
};
SidebarTrigger.displayName = "SidebarTrigger";

const SidebarRail = ({
  className,
  ...props
}: React.ComponentProps<"button">) => {
  const { toggleSidebar } = useSidebar();

  return (
    <button
      type="button"
      data-sidebar="rail"
      aria-label="Toggle Sidebar"
      tabIndex={-1}
      onClick={toggleSidebar}
      title="Toggle Sidebar"
      className={cn(
        "absolute inset-y-0 z-20 hidden w-4 -translate-x-1/2 transition-all ease-linear after:absolute after:inset-y-0 after:left-1/2 after:w-[2px] hover:after:bg-slate-subtle border group-data-[side=left]:-right-4 group-data-[side=right]:left-0 sm:flex",
        "in-data-[side=left]:cursor-w-resize in-data-[side=right]:cursor-e-resize",
        "[[data-side=left][data-state=collapsed]_&]:cursor-e-resize [[data-side=right][data-state=collapsed]_&]:cursor-w-resize",
        "group-data-[collapsible=offcanvas]:translate-x-0 group-data-[collapsible=offcanvas]:after:left-full hover:group-data-[collapsible=offcanvas]:bg-slate-subtle",
        "[[data-side=left][data-collapsible=offcanvas]_&]:-right-2",
        "[[data-side=right][data-collapsible=offcanvas]_&]:-left-2",
        className,
      )}
      {...props}
    />
  );
};
SidebarRail.displayName = "SidebarRail";

const SidebarInset = ({
  className,
  ...props
}: React.ComponentProps<"main">) => {
  return (
    <main
      className={cn(
        "relative flex min-h-svh flex-1 flex-col",
        "peer-data-[variant=inset]:min-h-[calc(100svh-(--spacing(4)))] md:peer-data-[variant=inset]:m-2 md:peer-data-[variant=inset]:peer-data-[state=collapsed]:ml-2 md:peer-data-[variant=inset]:ml-0 md:peer-data-[variant=inset]:rounded-xl md:peer-data-[variant=inset]:shadow-sm",
        className,
      )}
      {...props}
    />
  );
};
SidebarInset.displayName = "SidebarInset";

const SidebarInput = ({
  className,
  ...props
}: React.ComponentProps<typeof Input>) => {
  return (
    <Input
      data-sidebar="input"
      className={cn(
        "h-8 w-full bg-slate-app shadow-none focus-visible:ring-2",
        className,
      )}
      {...props}
    />
  );
};
SidebarInput.displayName = "SidebarInput";

const SidebarHeader = ({
  className,
  ...props
}: React.ComponentProps<"div">) => {
  return (
    <div
      data-sidebar="header"
      className={cn("flex flex-col gap-2 pt-4 px-4", className)}
      {...props}
    />
  );
};
SidebarHeader.displayName = "SidebarHeader";

const SidebarFooter = ({
  className,
  ...props
}: React.ComponentProps<"div">) => {
  return (
    <div
      data-sidebar="footer"
      className={cn("flex flex-col gap-2 pb-4 px-2", className)}
      {...props}
    />
  );
};
SidebarFooter.displayName = "SidebarFooter";

const SidebarSeparator = ({
  className,
  ...props
}: React.ComponentProps<typeof Separator>) => {
  return (
    <Separator
      data-sidebar="separator"
      className={cn(
        "my-1 mx-1 w-auto bg-slate-subtle border border-slate-4 dark:border-slatedark-4",
        className,
      )}
      {...props}
    />
  );
};
SidebarSeparator.displayName = "SidebarSeparator";

const SidebarContent = ({
  className,
  ...props
}: React.ComponentProps<"div">) => {
  return (
    <div
      data-sidebar="content"
      className={cn(
        "flex min-h-0 flex-1 flex-col py-8 px-2 gap-6 overflow-auto group-data-[collapsible=icon]:overflow-hidden",
        className,
      )}
      {...props}
    />
  );
};
SidebarContent.displayName = "SidebarContent";

const SidebarGroupCollapsibleContext = React.createContext<string | undefined>(
  undefined,
);

function toggleGroup(group: string) {
  const sidebarState = JSON.parse(
    localStorage.getItem("sidebarState") || "{}",
  ) as Record<string, boolean>;
  sidebarState[group] = !sidebarState[group];
  localStorage.setItem("sidebarState", JSON.stringify(sidebarState));
}

const SidebarGroup = ({
  className,
  collapsible,
  ...props
}: React.ComponentProps<"details"> & {
  collapsible?: string;
}) => {
  const Comp = collapsible ? "details" : "div";

  return (
    <SidebarGroupCollapsibleContext value={collapsible}>
      <Comp
        data-sidebar="group"
        data-sidebar-id={collapsible}
        {...(collapsible ? { open: true } : {})}
        className={cn(
          "relative flex w-full min-w-0 flex-col gap-4 px-2 group",
          className,
        )}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        {...(props as React.ComponentProps<any>)}
      />
    </SidebarGroupCollapsibleContext>
  );
};
SidebarGroup.displayName = "SidebarGroup";

const SidebarGroupLabel = ({
  className,
  children,
  ...props
}: React.ComponentProps<"summary">) => {
  const collapsible = React.use(SidebarGroupCollapsibleContext);
  const Comp = collapsible ? "summary" : "div";

  return (
    <Comp
      data-sidebar="group-label"
      className={cn(
        "text-slate-dim instrument-serif text-xl",
        collapsible && "hover:text-slate-normal",
        "duration-200 flex h-8 shrink-0 items-center rounded-md px-2 font-semibold outline-hidden transition-[margin,opa] ease-linear focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0",
        "group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0",
        "flex items-center gap-2 justify-between w-full",
        "select-none text-left",
        className,
      )}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      {...(props as React.ComponentProps<any>)}
      onClick={() => {
        if (collapsible) {
          toggleGroup(collapsible);
        }
      }}
    >
      {children}
      {collapsible && (
        <div className="flex items-center justify-center h-4 w-4">
          <ChevronDownIcon className="group-open:rotate-180" />
        </div>
      )}
    </Comp>
  );
};
SidebarGroupLabel.displayName = "SidebarGroupLabel";

const SidebarGroupAction = ({
  className,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> & { asChild?: boolean }) => {
  const Comp = asChild ? Slot : "button";
  const { setOpenMobile } = useSidebar();

  return (
    <Comp
      data-sidebar="group-action"
      className={cn(
        "flex",
        "aspect-square",
        "w-6",
        "shrink-0",
        "items-center",
        "justify-center",
        "rounded-md",
        "p-0",
        "text-slate-dim",
        "outline-hidden",
        "transition-transform",
        "bg-slate-ghost",
        "focus-visible:ring-2",
        "[&>svg]:size-4",
        "[&>svg]:shrink-0",
        "group-data-[collapsible=icon]:hidden",
        className,
      )}
      {...mergeProps(props, {
        onClick: () => setOpenMobile(false),
      })}
    />
  );
};
SidebarGroupAction.displayName = "SidebarGroupAction";

const SidebarGroupContent = ({
  className,
  ...props
}: React.ComponentProps<"div">) => (
  <div
    data-sidebar="group-content"
    className={cn("w-full text-sm", className)}
    {...props}
  />
);
SidebarGroupContent.displayName = "SidebarGroupContent";

const SidebarMenu = ({ className, ...props }: React.ComponentProps<"ul">) => (
  <ul
    data-sidebar="menu"
    className={cn("flex w-full min-w-0 flex-col gap-1", className)}
    {...props}
  />
);
SidebarMenu.displayName = "SidebarMenu";

const SidebarMenuItem = ({
  className,
  ...props
}: React.ComponentProps<"li">) => {
  return (
    <li
      data-sidebar="menu-item"
      className={cn("group/menu-item relative select-none", className)}
      {...props}
    />
  );
};
SidebarMenuItem.displayName = "SidebarMenuItem";

const sidebarMenuButtonVariants = cva("", {
  variants: {
    variant: {
      default: "",
    },
    size: {
      default: "min-h-8 text-sm",
      sm: "min-h-7 text-xs",
      lg: "min-h-12 text-sm group-data-[collapsible=icon]:p-0!",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "default",
  },
});

const SidebarMenuButton = ({
  asChild = false,
  isActive = false,
  variant = "default",
  size = "default",
  tooltip,
  className,
  ...props
}: React.ComponentProps<"button"> & {
  asChild?: boolean;
  isActive?: boolean;
  tooltip?: string | React.ComponentProps<typeof TooltipContent>;
} & VariantProps<typeof sidebarMenuButtonVariants>) => {
  const Comp = asChild ? Slot : "button";
  const { isMobile, state, setOpenMobile } = useSidebar();

  const button = (
    <Comp
      data-sidebar="menu-button"
      data-size={size}
      data-active={isActive}
      className={cn(
        "peer/menu-button flex w-full items-center gap-2 overflow-hidden rounded-md p-2",
        "text-left text-sm outline-hidden transition-[width,height,padding]",
        "bg-slate-ghost hover:text-slate-normal",
        "focus-visible:ring-2 active:bg-slate-subtle active:text-slate-normal",
        "disabled:pointer-events-none disabled:opacity-50",
        "group-has-data-[sidebar=menu-action]/menu-item:pr-8",
        "aria-disabled:pointer-events-none",
        "aria-disabled:opacity-50",
        "data-[active=true]:bg-slate-action",
        "data-[active=true]:text-slate-normal",
        "group-data-[collapsible=icon]:size-8!",
        "group-data-[collapsible=icon]:p-2!",
        "[&>span:last-child]:truncate",
        "[&>svg]:size-4",
        "[&>svg]:shrink-0",
        sidebarMenuButtonVariants({ variant, size }),
        className,
      )}
      {...mergeProps(props, {
        onClick: () => setOpenMobile(false),
      })}
    />
  );

  if (!tooltip) {
    return button;
  }

  if (typeof tooltip === "string") {
    tooltip = {
      children: tooltip,
    };
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent
        side="right"
        align="center"
        hidden={state !== "collapsed" || isMobile}
        {...tooltip}
      />
    </Tooltip>
  );
};
SidebarMenuButton.displayName = "SidebarMenuButton";

const SidebarMenuAction = ({
  className,
  asChild = false,
  showOnHover = false,
  ...props
}: React.ComponentProps<"button"> & {
  asChild?: boolean;
  showOnHover?: boolean;
}) => {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-sidebar="menu-action"
      className={cn(
        "absolute right-1 top-1.5 flex aspect-square w-5 items-center justify-center rounded-md p-0 text-slate-dim outline-hidden transition-transform hover:bg-slate-subtle hover:text-slate-normal focus-visible:ring-2 peer-hover/menu-button:text-slate-normal [&>svg]:size-4 [&>svg]:shrink-0",
        // Increases the hit area of the button on mobile.
        "after:absolute after:-inset-2 md:after:hidden",
        "peer-data-[size=sm]/menu-button:top-1",
        "peer-data-[size=default]/menu-button:top-1.5",
        "peer-data-[size=lg]/menu-button:top-2.5",
        "group-data-[collapsible=icon]:hidden",
        showOnHover &&
          "group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100 data-[state=open]:opacity-100 peer-data-[active=true]/menu-button:text-slate-normal md:opacity-0",
        className,
      )}
      {...props}
    />
  );
};
SidebarMenuAction.displayName = "SidebarMenuAction";

const SidebarMenuBadge = ({
  className,
  ...props
}: React.ComponentProps<"div">) => (
  <div
    data-sidebar="menu-badge"
    className={cn(
      "absolute right-1 flex h-5 min-w-5 items-center justify-center rounded-md px-1 text-xs font-medium tabular-nums text-slate-dim select-none pointer-events-none",
      "peer-hover/menu-button:text-slate-normal peer-data-[active=true]/menu-button:text-slate-normal",
      "peer-data-[size=sm]/menu-button:top-1",
      "peer-data-[size=default]/menu-button:top-1.5",
      "peer-data-[size=lg]/menu-button:top-2.5",
      "group-data-[collapsible=icon]:hidden",
      className,
    )}
    {...props}
  />
);
SidebarMenuBadge.displayName = "SidebarMenuBadge";

const SidebarMenuSkeleton = ({
  className,
  showIcon = false,
  ...props
}: React.ComponentProps<"div"> & {
  showIcon?: boolean;
}) => {
  // Random width between 50 to 90%.
  // eslint-disable-next-line @eslint-react/naming-convention/use-state
  const [width] = React.useState(() => Math.floor(Math.random() * 40) + 50);

  return (
    <div
      data-sidebar="menu-skeleton"
      className={cn("rounded-md h-8 flex gap-2 px-2 items-center", className)}
      {...props}
    >
      {showIcon && (
        <Skeleton
          className="size-4 rounded-md"
          data-sidebar="menu-skeleton-icon"
        />
      )}
      <Skeleton
        className="h-4 flex-1 max-w-(--skeleton-width)"
        data-sidebar="menu-skeleton-text"
        style={
          {
            "--skeleton-width": width,
          } as React.CSSProperties
        }
      />
    </div>
  );
};
SidebarMenuSkeleton.displayName = "SidebarMenuSkeleton";

const SidebarMenuSub = ({
  className,
  ...props
}: React.ComponentProps<"ul">) => (
  <ul
    data-sidebar="menu-sub"
    className={cn(
      "mx-3.5 flex min-w-0 translate-x-px flex-col gap-1 border-l border-slate-6 dark:border-slatedark-6 px-2.5 py-0.5",
      "group-data-[collapsible=icon]:hidden",
      className,
    )}
    {...props}
  />
);
SidebarMenuSub.displayName = "SidebarMenuSub";

const SidebarMenuSubItem = (props: React.ComponentProps<"li">) => (
  <li {...props} />
);
SidebarMenuSubItem.displayName = "SidebarMenuSubItem";

const SidebarMenuSubButton = ({
  asChild = false,
  size = "md",
  isActive,
  className,
  ...props
}: React.ComponentProps<"a"> & {
  asChild?: boolean;
  size?: "sm" | "md";
  isActive?: boolean;
}) => {
  const Comp = asChild ? Slot : "a";

  return (
    <Comp
      data-sidebar="menu-sub-button"
      data-size={size}
      data-active={isActive}
      className={cn(
        "flex h-7 min-w-0 -translate-x-px items-center gap-2 overflow-hidden rounded-md px-2 text-slate-dim outline-hidden hover:bg-slate-subtle hover:text-slate-normal focus-visible:ring-2 active:bg-slate-subtle active:text-slate-normal disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0 [&>svg]:text-slate-normal",
        "data-[active=true]:bg-slate-subtle data-[active=true]:text-slate-normal",
        size === "sm" && "text-xs",
        size === "md" && "text-sm",
        "group-data-[collapsible=icon]:hidden",
        className,
      )}
      {...props}
    />
  );
};
SidebarMenuSubButton.displayName = "SidebarMenuSubButton";

export {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInput,
  SidebarInset,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
};
